import {FC, useEffect, useMemo, useState} from 'react'
import {api, Bed, AuthUser, Listing, OccupancySummary, ApiError, BedStatusValue, BedTypeValue, BedTypeCatalog, listingImageSrc} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const TYPE_LABEL: Record<BedTypeValue, string> = {TWIN: 'Twin', FULL: 'Full', QUEEN: 'Queen'}
const TYPE_TONE: Record<BedTypeValue, string> = {TWIN: '#039540', FULL: '#7c4dff', QUEEN: '#0aa37f'}

const STATUS_META: Record<BedStatusValue, {label: string; color: string; bg: string}> = {
  AVAILABLE: {label: 'Available', color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  OCCUPIED: {label: 'Occupied', color: '#2b4eff', bg: 'rgba(43,78,255,0.12)'},
  RESERVED: {label: 'Reserved', color: '#b8770a', bg: 'rgba(255,168,0,0.16)'},
  OUT_OF_SERVICE: {label: 'Out of service', color: '#7e8299', bg: 'rgba(126,130,153,0.15)'},
}

const SETTABLE: Exclude<BedStatusValue, 'OCCUPIED'>[] = ['AVAILABLE', 'RESERVED', 'OUT_OF_SERVICE']

const SummaryCard: FC<{label: string; value: number; tone?: string}> = ({label, value, tone}) => (
  <div className='col' style={{minWidth: 130}}>
    <div className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 6px 18px rgba(27,27,41,0.05)', height: '100%'}}>
      <div style={{fontSize: 28, fontWeight: 800, color: tone ?? INK, lineHeight: 1}}>{value}</div>
      <div style={{fontSize: 12, color: MUTED, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>{label}</div>
    </div>
  </div>
)

const BedsPage: FC = () => {
  const toast = useToast()
  const [beds, setBeds] = useState<Bed[]>([])
  const [rooms, setRooms] = useState<Listing[]>([])
  const [bedTypes, setBedTypes] = useState<BedTypeCatalog[]>([])
  const [summary, setSummary] = useState<OccupancySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [assignTo, setAssignTo] = useState<Bed | null>(null)
  const [addTo, setAddTo] = useState<BedTypeCatalog | null>(null)
  const [editBed, setEditBed] = useState<Bed | null>(null)
  const [search, setSearch] = useState('')
  const [statusF, setStatusF] = useState('')
  const [typeF, setTypeF] = useState('')
  const [roomF, setRoomF] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [b, s, r, bt] = await Promise.all([api.listBeds(), api.bedsSummary(), api.adminListings(), api.bedTypes()])
      setBeds(b)
      setSummary(s)
      setRooms(r)
      setBedTypes(bt)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load beds')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const refreshSummary = () => void api.bedsSummary().then(setSummary)
  const applyUpdate = (updated: Bed) => {
    setBeds((list) => list.map((b) => (b.id === updated.id ? updated : b)))
    refreshSummary()
  }

  const doStatus = async (bed: Bed, status: Exclude<BedStatusValue, 'OCCUPIED'>) => {
    setBusyId(bed.id)
    try {
      applyUpdate(await api.setBedStatus(bed.id, status))
      toast.success(`Bed ${bed.code} set to ${status.replace('_', ' ').toLowerCase()}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update bed')
    } finally {
      setBusyId(null)
    }
  }

  const doUnassign = async (bed: Bed) => {
    setBusyId(bed.id)
    try {
      applyUpdate(await api.unassignBed(bed.id))
      toast.success(`Bed ${bed.code} is now free.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not unassign')
    } finally {
      setBusyId(null)
    }
  }

  const doRemove = async (bed: Bed) => {
    const ok = await confirmDialog({
      title: `Delete bed ${bed.code}?`,
      text: 'This removes the bed from the room.',
      confirmText: 'Delete',
      danger: true,
    })
    if (!ok) return
    setBusyId(bed.id)
    try {
      await api.deleteBed(bed.id)
      setBeds((list) => list.filter((b) => b.id !== bed.id))
      refreshSummary()
      toast.success(`Bed ${bed.code} deleted.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not delete bed')
    } finally {
      setBusyId(null)
    }
  }

  // Group beds by BED TYPE (bed type → bed → room), in catalog order; a trailing
  // "No bed type" group catches any legacy untyped bed. Search/status/room filter
  // the beds; the type filter limits which sections show. With any active filter,
  // empty sections are hidden.
  const bedFilterActive = !!(search.trim() || statusF || typeF || roomF)
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (bed: Bed) => {
      if (statusF && bed.status !== statusF) return false
      if (typeF && bed.type !== typeF) return false
      if (roomF && bed.room?.id !== roomF) return false
      if (q) {
        const occ = bed.occupant ? `${bed.occupant.firstName} ${bed.occupant.lastName}` : ''
        if (!`${bed.code} ${bed.level ?? ''} ${occ}`.toLowerCase().includes(q)) return false
      }
      return true
    }
    const byType = new Map<string, Bed[]>()
    for (const bed of beds) {
      if (!matches(bed)) continue
      const key = bed.type ?? '∅'
      ;(byType.get(key) ?? byType.set(key, []).get(key)!).push(bed)
    }
    let ordered: {bedType: BedTypeCatalog | null; beds: Bed[]}[] = bedTypes.map((t) => ({
      bedType: t,
      beds: byType.get(t.key) ?? [],
    }))
    const untyped = byType.get('∅')
    if (untyped?.length) ordered.push({bedType: null, beds: untyped})
    if (bedFilterActive) ordered = ordered.filter((g) => g.beds.length > 0)
    return ordered
  }, [beds, bedTypes, search, statusF, typeF, roomF, bedFilterActive])

  return (
    <div>
      <PageHead icon='🛏️' eyebrow='Operations' title='Beds' sub='Beds grouped by bed type — each bed is assigned to a room. Assign renters, manage availability, add or remove beds.' />

      {summary && (
        <div className='row g-3 mb-5'>
          <SummaryCard label='Total' value={summary.total} />
          <SummaryCard label='Available' value={summary.available} tone='#1f9d5b' />
          <SummaryCard label='Occupied' value={summary.occupied} tone='#2b4eff' />
          <SummaryCard label='Reserved' value={summary.reserved} tone='#b8770a' />
          <SummaryCard label='Out of service' value={summary.outOfService} tone={MUTED} />
        </div>
      )}

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search by code, level or occupant…'>
        <FilterSelect value={statusF} onChange={setStatusF} ariaLabel='Status'
          options={[{label: 'All statuses', value: ''}, {label: 'Available', value: 'AVAILABLE'}, {label: 'Occupied', value: 'OCCUPIED'}, {label: 'Reserved', value: 'RESERVED'}, {label: 'Out of service', value: 'OUT_OF_SERVICE'}]} />
        <FilterSelect value={typeF} onChange={setTypeF} ariaLabel='Type'
          options={[{label: 'All types', value: ''}, {label: 'Twin', value: 'TWIN'}, {label: 'Full', value: 'FULL'}, {label: 'Queen', value: 'QUEEN'}]} />
        <FilterSelect value={roomF} onChange={setRoomF} ariaLabel='Room'
          options={[{label: 'All rooms', value: ''}, ...rooms.map((r) => ({label: r.name, value: r.id}))]} />
      </ListToolbar>

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading beds…</div>
      ) : error ? (
        <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
      ) : groups.length === 0 ? (
        <div style={{background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>No beds match your search.</div>
      ) : (
        groups.map(({bedType, beds: typeBeds}) => (
          <section key={bedType?.id ?? 'untyped'} style={{marginBottom: 32}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap'}}>
              <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>
                {bedType ? `${bedType.label} Beds` : 'No bed type'}
                <span style={{fontSize: 13, fontWeight: 600, color: MUTED, marginLeft: 10}}>
                  {bedType ? `$${Number(bedType.pricePerMonth).toFixed(0)}/month · ` : ''}{typeBeds.length} {typeBeds.length === 1 ? 'bed' : 'beds'}
                </span>
              </h2>
              {bedType && <button onClick={() => setAddTo(bedType)} style={btnGhost}>+ Add bed</button>}
            </div>

            {typeBeds.length === 0 ? (
              <div style={{color: MUTED, fontSize: 14, padding: '6px 2px'}}>No {bedType?.label ?? ''} beds yet.</div>
            ) : (
              <div className='row g-4 ccp-stagger'>
                {typeBeds.map((bed) => {
                  const sm = STATUS_META[bed.status]
                  const busy = busyId === bed.id
                  return (
                    <div className='col-12 col-sm-6 col-lg-3' key={bed.id}>
                      <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', borderTop: `4px solid ${TYPE_TONE[bed.type]}`, height: '100%', display: 'flex', flexDirection: 'column'}}>
                        <div style={{padding: '20px 20px 16px'}}>
                          <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
                            <span style={{fontSize: 30, fontWeight: 800, color: INK}}>{bed.code}</span>
                            <span style={{fontSize: 13, color: MUTED, fontWeight: 600}}>{bed.room?.name ?? 'No room'}</span>
                          </div>
                          {bed.level && <div style={{fontSize: 12, color: MUTED, marginTop: 2}}>{bed.level}</div>}
                          <div style={{fontSize: 14, fontWeight: 800, color: ACCENT, marginTop: 4}}>${Number(bed.pricePerMonth).toFixed(0)}<span style={{fontSize: 12, fontWeight: 600, color: MUTED}}>/month</span></div>
                          <span style={{display: 'inline-block', marginTop: 10, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: sm.color, background: sm.bg, borderRadius: 999, padding: '4px 10px'}}>{sm.label}</span>
                          <div style={{marginTop: 12, minHeight: 38}}>
                            {bed.occupant ? (
                              <div style={{fontSize: 13, color: INK}}>
                                <span style={{color: MUTED}}>Occupant: </span>
                                <b>{bed.occupant.firstName} {bed.occupant.lastName}</b>
                              </div>
                            ) : (
                              <div style={{fontSize: 13, color: MUTED}}>No occupant</div>
                            )}
                          </div>
                        </div>

                        <div style={{marginTop: 'auto', padding: '12px 16px', borderTop: '1px solid #f1f3f7', display: 'flex', flexDirection: 'column', gap: 8}}>
                          {bed.occupant ? (
                            <button onClick={() => doUnassign(bed)} disabled={busy} style={btnDanger}>{busy ? <Spinner size={13} /> : 'Unassign'}</button>
                          ) : (
                            <button onClick={() => setAssignTo(bed)} disabled={busy} style={btnPrimary}>Assign renter</button>
                          )}
                          <select
                            value={SETTABLE.includes(bed.status as never) ? bed.status : ''}
                            onChange={(e) => doStatus(bed, e.target.value as Exclude<BedStatusValue, 'OCCUPIED'>)}
                            disabled={busy}
                            style={{width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, color: INK, background: '#fff', cursor: 'pointer'}}
                          >
                            {bed.status === 'OCCUPIED' && <option value='' disabled>Set status…</option>}
                            {SETTABLE.map((s) => (
                              <option key={s} value={s}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                          <div style={{display: 'flex', gap: 8}}>
                            <button onClick={() => setEditBed(bed)} disabled={busy} style={{...btnGhost, flex: 1}}>Edit</button>
                            <button onClick={() => doRemove(bed)} disabled={busy || bed.status === 'OCCUPIED'} title={bed.status === 'OCCUPIED' ? 'Unassign before deleting' : 'Delete bed'} style={{...btnGhost, flex: 1, color: '#d9214e', opacity: bed.status === 'OCCUPIED' ? 0.5 : 1}}>Delete</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        ))
      )}

      {assignTo && (
        <ModalPortal><AssignModal
          bed={assignTo}
          onClose={() => setAssignTo(null)}
          onAssigned={(b) => {
            applyUpdate(b)
            setAssignTo(null)
            toast.success(`${b.occupant?.firstName ?? 'Renter'} ${b.occupant?.lastName ?? ''} assigned to bed ${b.code}.`)
          }}
        /></ModalPortal>
      )}

      {addTo && (
        <ModalPortal><AddBedModal
          bedType={addTo}
          onClose={() => setAddTo(null)}
          onAdded={(b) => {
            setBeds((list) => [...list, b])
            refreshSummary()
            setAddTo(null)
            toast.success(`Bed ${b.code} added.`)
          }}
        /></ModalPortal>
      )}

      {editBed && (
        <ModalPortal><EditBedModal
          bed={editBed}
          onClose={() => setEditBed(null)}
          onSaved={(b) => {
            setBeds((list) => list.map((x) => (x.id === b.id ? b : x)))
            setEditBed(null)
            toast.success(`Bed ${b.code} updated.`)
          }}
        /></ModalPortal>
      )}
    </div>
  )
}

// ─── Assign renter modal ────────────────────────────────────────────────
const AssignModal: FC<{bed: Bed; onClose: () => void; onAssigned: (b: Bed) => void}> = ({bed, onClose, onAssigned}) => {
  const [renters, setRenters] = useState<AuthUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api
      .listUsers('RENTER')
      .then((r) => setRenters(r.filter((u) => u.accountStatus === 'ACTIVE')))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Failed to load renters'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return renters
    return renters.filter((u) => `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q))
  }, [renters, search])

  const assign = async (userId: string) => {
    setBusy(userId)
    setErr(null)
    try {
      onAssigned(await api.assignBed(bed.id, userId))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not assign')
      setBusy(null)
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{...modalCard, maxWidth: 460}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Assign bed {bed.code}</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>{bed.room ? `${bed.room.name} · ` : ''}Choose a renter to assign.</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Search renters…' style={{width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14, marginBottom: 14, boxSizing: 'border-box', outline: 'none'}} />
        <div style={{maxHeight: 320, overflowY: 'auto'}}>
          {loading ? (
            <div style={{padding: 20, textAlign: 'center', color: MUTED}}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 20, textAlign: 'center', color: MUTED}}>No renters found.</div>
          ) : (
            filtered.map((u) => (
              <div key={u.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 4px', borderBottom: '1px solid #f1f3f7'}}>
                <div>
                  <div style={{fontWeight: 600, color: INK, fontSize: 14}}>{u.firstName} {u.lastName}</div>
                  <div style={{color: MUTED, fontSize: 13}}>{u.email}</div>
                </div>
                <button onClick={() => assign(u.id)} disabled={!!busy} style={{...btnPrimary, minWidth: 64}}>{busy === u.id ? <Spinner size={13} /> : 'Assign'}</button>
              </div>
            ))
          )}
        </div>
        <div style={{textAlign: 'right', marginTop: 16}}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ─── Add bed modal ──────────────────────────────────────────────────────
// A bed is created for a bed type. (A room can be assigned to it later.)
const AddBedModal: FC<{bedType: BedTypeCatalog; onClose: () => void; onAdded: (b: Bed) => void}> = ({bedType, onClose, onAdded}) => {
  const [level, setLevel] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    setErr(null)
    setBusy(true)
    try {
      onAdded(await api.createBed({bedTypeId: bedType.id, level: level.trim() || undefined, code: code.trim() || undefined}))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not add bed')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{...modalCard, maxWidth: 420}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Add {bedType.label} bed</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>${Number(bedType.pricePerMonth).toFixed(0)}/month</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

        <Field label='Level / label (optional)'>
          <input value={level} onChange={(e) => setLevel(e.target.value)} placeholder='e.g. Bunk 3 · Top' style={inp} />
        </Field>
        <Field label='Code (optional — auto-generated)'>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder='e.g. 9Q' style={inp} />
        </Field>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={{...btnPrimary, minWidth: 96}} disabled={busy}>{busy ? <Spinner size={14} /> : 'Add bed'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Edit bed (level only — type + price come from the room's bed type) ──
const EditBedModal: FC<{bed: Bed; onClose: () => void; onSaved: (b: Bed) => void}> = ({bed, onClose, onSaved}) => {
  const toast = useToast()
  const [level, setLevel] = useState(bed.level ?? '')
  const [keyFob, setKeyFob] = useState(bed.keyFob ?? '')
  const [photo, setPhoto] = useState<string | null>(bed.photo ?? null)
  const [boardingPass, setBoardingPass] = useState<string | null>(bed.boardingPass ?? null)
  const [uploading, setUploading] = useState<'photo' | 'pass' | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const uploadTo = async (file: File | undefined, kind: 'photo' | 'pass', set: (k: string) => void, label: string) => {
    if (!file) return
    setUploading(kind)
    try {
      const stored = await api.uploadFile(file)
      set(stored.key)
      toast.success(`${label} uploaded — remember to save.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(null)
    }
  }

  const submit = async () => {
    setErr(null)
    setBusy(true)
    try {
      onSaved(await api.updateBed(bed.id, {level: level.trim(), keyFob: keyFob.trim() || null, photo, boardingPass}))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save bed')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{...modalCard, maxWidth: 420}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Edit bed {bed.code}</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>{bed.room?.name ?? 'No room'} · {TYPE_LABEL[bed.type]} · ${Number(bed.pricePerMonth).toFixed(0)}/month</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

        <Field label='Level / label'>
          <input value={level} onChange={(e) => setLevel(e.target.value)} placeholder='e.g. Bunk 3 · Top' style={inp} />
        </Field>

        <Field label='Key fob (sent in the welcome email)'>
          <input value={keyFob} onChange={(e) => setKeyFob(e.target.value)} placeholder={`e.g. Black key fob · ${bed.code} boarding pass keychain`} style={inp} />
        </Field>

        <Field label={`Bed photo (show the "${bed.code}" label in the shot)`}>
          <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
            <div style={{width: 92, height: 69, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f4f6fa', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: 12}}>
              {photo ? <img src={listingImageSrc(photo)} alt={`Bed ${bed.code}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : 'No photo'}
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
              <label style={{...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading === 'photo' ? 0.7 : 1}}>
                {uploading === 'photo' ? <Spinner size={13} /> : '📷'} {photo ? 'Replace photo' : 'Upload photo'}
                <input type='file' accept='image/*' hidden disabled={!!uploading} onChange={(e) => uploadTo(e.target.files?.[0], 'photo', setPhoto, 'Photo')} />
              </label>
              {photo && <button type='button' onClick={() => setPhoto(null)} style={{...btnDanger, alignSelf: 'flex-start'}}>Remove</button>}
            </div>
          </div>
        </Field>

        <Field label={`Boarding pass (the ${bed.code} pass — attached to the welcome email)`}>
          <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
            <div style={{width: 92, height: 69, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f4f6fa', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: 12}}>
              {boardingPass ? <img src={listingImageSrc(boardingPass)} alt={`Boarding pass ${bed.code}`} style={{width: '100%', height: '100%', objectFit: 'contain'}} /> : 'None'}
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
              <label style={{...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading === 'pass' ? 0.7 : 1}}>
                {uploading === 'pass' ? <Spinner size={13} /> : '🎫'} {boardingPass ? 'Replace pass' : 'Upload pass'}
                <input type='file' accept='image/*' hidden disabled={!!uploading} onChange={(e) => uploadTo(e.target.files?.[0], 'pass', setBoardingPass, 'Boarding pass')} />
              </label>
              {boardingPass && <button type='button' onClick={() => setBoardingPass(null)} style={{...btnDanger, alignSelf: 'flex-start'}}>Remove</button>}
            </div>
          </div>
        </Field>

        <p style={{fontSize: 12, color: MUTED, margin: '0 0 4px'}}>Type &amp; price are set by the room's bed type — change them on Bed Types.</p>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={{...btnPrimary, minWidth: 110}} disabled={busy || !!uploading}>{busy ? <Spinner size={14} /> : 'Save changes'}</button>
        </div>
      </div>
    </div>
  )
}

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)

const modalBackdrop: React.CSSProperties = {position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}
const modalCard: React.CSSProperties = {background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {BedsPage}
